import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { AiMetadataStatus, useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { AiSmartActionStatus, useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useUser } from '@/modules/viewer/composables/useUser';

export const useSummaryFeedback = () => {
  const itemStore = useViewerItemStore();
  const aiMetadataStore = useAiMetadataStore();
  const smartActionsStore = useSmartActionsStore();
  const { isCurrentItemOwner } = useUser();
  const { description } = storeToRefs(itemStore);

  const isSmartActionDescription = computed(() => {
    return !!smartActionsStore.actionType && smartActionsStore.actionStatus === AiSmartActionStatus.DONE;
  });

  const isAiMetadataDescription = computed(() => {
    return !isSmartActionDescription.value && aiMetadataStore.status === AiMetadataStatus.DONE;
  });

  const descriptionProvider = computed((): string | undefined => {
    if (isSmartActionDescription.value) {
      return smartActionsStore.provider || 'openai';
    }
    if (isAiMetadataDescription.value) {
      return aiMetadataStore.provider || 'sieve';
    }
    return undefined;
  });

  const feedbackState = computed((): 'positive' | 'negative' | null => {
    if (isSmartActionDescription.value) {
      return smartActionsStore.feedback ?? null;
    }
    if (isAiMetadataDescription.value) {
      return aiMetadataStore.feedback ?? null;
    }
    return null;
  });

  const showFeedback = computed(() => {
    return isCurrentItemOwner.value && (isSmartActionDescription.value || isAiMetadataDescription.value) && description.value && description.value.length > 0;
  });

  const handleFeedback = (feedbackType: 'positive' | 'negative' | null) => {
    if (isSmartActionDescription.value) {
      smartActionsStore.setSmartActionFeedback(feedbackType);
    } else if (isAiMetadataDescription.value) {
      aiMetadataStore.setAiMetadataFeedback(feedbackType);
    }
  };

  const smartActionType = computed(() => smartActionsStore.actionType);

  return {
    descriptionProvider,
    feedbackState,
    handleFeedback,
    isSmartActionDescription,
    showFeedback,
    smartActionType,
  };
};
